import { DecoratedFormProps, InjectedFormProps, reduxForm, formValueSelector } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import {
    email,
    maxLength,
    required,
    Validators,
    numeric,
    minLength,
    minPhoneLength,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { FieldInputMask } from 'src/components/redux-form/fields/FieldInputMask';
import { FieldMultiDropdown } from 'src/components/redux-form/fields/FieldMultiDropdown';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/providers-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistProvider } from 'src/store/actions/provider-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { DropZoneField } from 'src/components/DropZoneField';

import { Paginated } from 'src/services/api-handlers/pagination';
import { IServiceLocation } from 'src/types/service-locations';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import InfoItem from '../../../../../components/InfoItem';
import Loader from '../../../../../components/Loader';
import { useSelector } from 'react-redux';
import { miniLoaderForSelector } from '../../../../../store/selectors/providers-selectors';
import { changeToZeroMoment } from '../../../../../helpers/timeHelpers';
import { numberWithCommas } from '../../../../../helpers/format';
import InfoRatesItem from '../../../../../components/InfoRatesItem';
import { FieldDropdown } from '../../../../../components/redux-form/fields/FieldDropdown';
import { IProvider } from '../../../../../types/provider';

export interface ProviderFormData {
    id?: string | number;
    userId?: string | number;
    fullName: string;
    employeeId?: string;
    rateAdminWorks: AnyObject[];
    rateAdvances?: AnyObject[];
    email: string;
    phone: string;
    annualSalary: number;
    serviceLocations: { value: number; label: string }[];
    serviceTypes: { value: number; label: string }[];
    providers: { value: number; label: string }[];
    npi: string;
    licenseDescription: string;
    license: string;
    certificates: string;
    isGetAdvance: boolean;
    isTest: boolean;
    isReferred: boolean;
    status: number;
    rateAdminWork: number;
    rateAdvance: number;
    rateAdvanceDate: string;
    staffTitle: string;
    rateAdminWorkDate: string;
    signDocName: string | null;
    signDocMimeType: string | null;
    referredByDropDown: SelectObject | undefined | null;
}

interface ProviderFormProps {
    signDoc: any;
    setSignDoc: (it: any) => void;
    files: any;
    providers: { title: string; id: number | string }[];
    setFiles: (it: []) => void;
    removeFile: (it: any) => void;
    serviceTypes: any;
    handleSub: (data: (e: any) => void, e: any) => void;
    serviceLocations: Paginated<IServiceLocation>;
}
type InjectedProps = InjectedFormProps<ProviderFormData, ProviderFormProps>;

const validators: Validators<ProviderFormData> = {
    email: [required({ message: 'Email is required.' }), email(), minLength({ min: 1 }), maxLength({ max: 255 })],
    fullName: [required({ message: 'Name is required.' }), maxLength({ max: 255 })],
    //employeeId: [required({ message: 'Employee ID is required.' }), maxLength({ max: 255 })],
    staffTitle: [maxLength({ max: 255 })],
    phone: [required({ message: 'Phone is required.' }), minPhoneLength({ min: 14 })],
    npi: [required({ message: 'NPI is required.' }), minLength({ min: 10 }), maxLength({ max: 10 }), numeric()],
    license: [required({ message: 'License Number is required.' }), maxLength({ max: 255 })],
    licenseDescription: [maxLength({ max: 255 })],
    certificates: [maxLength({ max: 255 })],
    rateAdminWork: [required({ message: 'Rate for Admin Work per Hour, $ is required.' }), maxLength({ max: 255 })],
    rateAdminWorkDate: [required({ message: 'Rate for Admin Work Date is required.' }), maxLength({ max: 255 })],
    rateAdvance: [required({ message: 'Rate for Advance per Hour, $ is required.' }), maxLength({ max: 255 })],
    rateAdvanceDate: [required({ message: 'Rate for Advance Date is required.' }), maxLength({ max: 255 })],
};

const ProviderForm: React.FunctionComponent<InjectedProps & ProviderFormProps> = ({
    handleSubmit,
    error,
    dirty,
    files,
    setFiles,
    handleSub,
    signDoc,
    setSignDoc,
    providers,
    removeFile,
    serviceTypes,
    serviceLocations,
    submitting,
    initialValues,
}: InjectedProps & ProviderFormProps) => {
    const isNew = !initialValues || !initialValues.id;
    let rateAdminWorks: AnyObject[] = [];
    //let rateAdvances: AnyObject[] = [];
    const signDocs = [];
    if (signDoc) {
        const signDocFile = {
            attach: signDoc,
            mimeType: initialValues.signDocMimeType,
            name: initialValues.signDocName,
        };
        signDocs.push(signDocFile);
    }

    const providerValues: SelectObject[] = [];
    providers.forEach((prov: Partial<any>) => {
        providerValues.push({ value: prov.id, label: `${prov.title}` });
    });
    providerValues.sort((a: SelectObject, b: SelectObject) => {
        const textA = a.label.toUpperCase();
        const textB = b.label.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
    });

    if (initialValues) {
        rateAdminWorks = initialValues.rateAdminWorks ? initialValues.rateAdminWorks : [];
        //rateAdvances = initialValues.rateAdvances ? initialValues.rateAdvances : [];
    }
    const miniLoaderFor = useSelector(miniLoaderForSelector);

    const selector = formValueSelector('provider-form');
    const isReferredChecked = useSelector((state: any) => selector(state, 'isReferred'));

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <div className={classNames(styles.box)}>
                <div className={classNames(styles.title)}>Contact Information</div>
                <div>
                    <FieldInput
                        name="id"
                        type="hidden"
                        wrapperConfig={{
                            classes: {
                                inputWrapper: 'm-0',
                            },
                        }}
                    />
                    <FieldInput
                        name="email"
                        type="email"
                        validate={validators.email}
                        wrapperConfig={{
                            label: 'Email *',
                        }}
                    />

                    <FieldInput
                        name="fullName"
                        type="text"
                        validate={validators.fullName}
                        wrapperConfig={{
                            label: 'Full Name *',
                        }}
                    />
                    <FieldInput
                        name="employeeId"
                        type="text"
                        validate={validators.employeeId}
                        wrapperConfig={{
                            label: 'Employee ID *',
                        }}
                    />
                    <FieldInputMask
                        mask={'+1 999-999-9999'}
                        name="phone"
                        type="phone"
                        validate={validators.phone}
                        wrapperConfig={{
                            label: 'Phone Number *',
                        }}
                    />
                </div>
            </div>

            <div className={classNames(styles.box, styles.staffBox)}>
                <div className={classNames(styles.title)}>Staff Information</div>
                <div>
                    <FieldInput
                        name="npi"
                        type="number"
                        validate={validators.npi}
                        wrapperConfig={{
                            label: 'NPI *',
                        }}
                    />
                    <FieldInput
                        name="license"
                        type="text"
                        validate={validators.license}
                        wrapperConfig={{
                            label: 'License Number *',
                        }}
                    />
                    <FieldInput
                        name="staffTitle"
                        type="text"
                        validate={validators.staffTitle}
                        wrapperConfig={{
                            label: 'Staff Title',
                        }}
                    />
                    <FieldInput
                        name="licenseDescription"
                        type="text"
                        validate={validators.licenseDescription}
                        wrapperConfig={{
                            label: 'License description',
                        }}
                    />
                    <FieldInput
                        name="certificates"
                        type="text"
                        validate={validators.certificates}
                        wrapperConfig={{
                            label: 'Certificates',
                        }}
                    />
                </div>
            </div>

            <div className={classNames(styles.box, styles.serviceBox)}>
                <div className={classNames(styles.title)}>Service Information</div>
                <div>
                    <FieldMultiDropdown
                        name="serviceLocations"
                        textField={'label'}
                        valueField={'value'}
                        data={serviceLocations.list}
                        validate={validators.serviceLocations}
                        wrapperConfig={{
                            label: 'Service Locations',
                        }}
                    />
                    <FieldMultiDropdown
                        name="serviceTypes"
                        textField={'label'}
                        valueField={'value'}
                        data={serviceTypes}
                        wrapperConfig={{
                            label: 'Service Types',
                        }}
                    />
                </div>
            </div>

            <div className={classNames(styles.box, styles.salaryBox)}>
                <div className={classNames(styles.title)}>Salary Information</div>
                <div>
                    <div className={classNames(styles.mTop)}>
                        <FieldInput
                            name="annualSalary"
                            type="number"
                            wrapperConfig={{
                                label: 'Annual Salary, $',
                            }}
                        />
                    </div>
                    <FieldInput
                        name="isGetAdvance"
                        type="checkbox"
                        validate={validators.isGetAdvance}
                        wrapperConfig={{
                            label: 'Gets an Advance?',
                            classes: {
                                inputWrapper: 'customCheckbox',
                            },
                        }}
                    />
                </div>
            </div>

            <div className={classNames(styles.box, styles.supervisionBox)}>
                <div className={classNames(styles.title)}>Supervision Information</div>
                <div>
                    <div className={classNames(styles.mTop)}>
                        <FieldInput
                            name="isTest"
                            type="checkbox"
                            validate={validators.isTest}
                            wrapperConfig={{
                                label: 'Is the provider created for test?',
                                classes: {
                                    inputWrapper: 'customCheckbox',
                                },
                            }}
                        />
                    </div>

                    <div className={classNames(styles.mTop)}>
                        <FieldMultiDropdown
                            name="providers"
                            textField={'label'}
                            valueField={'value'}
                            data={providers}
                            wrapperConfig={{
                                label: 'Providers for a supervisor',
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={classNames(styles.box, styles.bonusBox)}>
                <div className={classNames(styles.title)}>Referral Bonus Information</div>
                <div>
                    <div className={classNames(styles.mTop)}>
                        <FieldInput
                            name="isReferred"
                            type="checkbox"
                            validate={validators.isReferred}
                            wrapperConfig={{
                                label: 'Is the provider referred?',
                                classes: {
                                    inputWrapper: 'customCheckbox',
                                },
                            }}
                        />
                    </div>

                    {isReferredChecked && (
                        <div>
                            <div className={classNames(styles.mTop)}>
                                <FieldDropdown
                                    name="referredByDropDown"
                                    data={providerValues}
                                    textField={'label'}
                                    valueField={'value'}
                                    validate={validators.referredByDropDown}
                                    wrapperConfig={{
                                        label: 'Select the referral provider',
                                    }}
                                />
                            </div>

                            <div className={classNames(styles.mTop)}>
                                <FieldInput
                                    name="referredAmount"
                                    type="number"
                                    wrapperConfig={{
                                        label: 'Enter the bonus amount, $',
                                    }}
                                />
                            </div>

                            <div className={classNames(styles.mTop)}>
                                <FieldInput
                                    name="referredHours"
                                    type="number"
                                    wrapperConfig={{
                                        label: 'Enter the number of needed billed hours',
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={classNames(styles.box, styles.rateAdminBox)}>
                <div className={classNames(styles.title)}>Rates for Admin Work</div>
                <div className={classNames(styles.fixBox)}>
                    {rateAdminWorks.map((itW: any, k) => (
                        <div key={k}>
                            <InfoRatesItem
                                title={''}
                                valueSum={numberWithCommas(itW.rateForAdminWork)}
                                valueDate={itW.createdAt ? changeToZeroMoment(itW.createdAt, 'MMM D, YYYY') : null}
                                value={`$${numberWithCommas(itW.rateForAdminWork)} per hour from ${
                                    itW.createdAt ? changeToZeroMoment(itW.createdAt, 'MMM D, YYYY') : null
                                }`}
                            />
                        </div>
                    ))}
                </div>
                <div>
                    <div className={classNames(styles.widthRow)}>
                        <FieldDate
                            name="rateAdminWorkDate"
                            type="text"
                            date={true}
                            validate={isNew ? validators.rateAdminWorkDate : undefined}
                            wrapperConfig={{
                                label: 'From *',
                            }}
                        />
                    </div>
                    <div className={classNames(styles.widthRow, styles.mr)}>
                        <FieldInput
                            name="rateAdminWork"
                            type="number"
                            validate={isNew ? validators.rateAdminWork : undefined}
                            wrapperConfig={{
                                label: 'Amount *',
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={classNames(styles.box, styles.documentBox)}>
                <div className={classNames(styles.title)}>Documents</div>
                <div>
                    <div className={'position-relative'}>
                        {miniLoaderFor['FILE'] ? <Loader noFull={true} /> : null}
                        <div className={classNames(styles.mTop)}>
                            <div>Sign Doc *</div>
                            <DropZoneField validatePhoto={true} single={true} files={signDocs} setFiles={setSignDoc} />
                        </div>
                        <div className={classNames(styles.mTop)}>
                            <div>Support Files</div>
                            <DropZoneField
                                files={files}
                                setFiles={setFiles}
                                removeFile={(id: string | number) => removeFile(id)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="button"
                    onClick={(e) => {
                        handleSub(handleSubmit, e);
                    }}
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.PROVIDERS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<ProviderFormData, ProviderFormProps>({
    form: 'provider-form',
    enableReinitialize: true,
    onSubmit: async (data: ProviderFormData, dispatch: Dispatch<any>, props: ProviderFormProps) => {
        if (data) {
        }

        await asyncPersistProvider(data, { files: props.files, signDoc: props.signDoc }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<ProviderFormData, ProviderFormProps>,
    ) {
        const isNew = !props.initialValues || !props.initialValues.id;

        toast.success(`Provider was successfully ${isNew ? 'created' : 'updated'}.`);
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(ProviderForm);
